import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { HugeiconsIcon } from "@hugeicons/react";
import { Loading03Icon } from "@hugeicons/core-free-icons";
import type { VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { buttonVariants } from "./button-variants";

function Button({
  className,
  variant = "default",
  size = "default",
  loading = false,
  disabled,
  focusableWhenDisabled,
  children,
  ...props
}: ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    /**
     * The action this button started is running (ui-guideline §3.4): the button
     * is disabled, keeps focus, and shows a spinner after its label, or in place
     * of its icon for an icon-only size. Pass the pending label (« Écriture… »)
     * as children; a still icon replaces the spinner under reduced motion.
     */
    loading?: boolean;
  }) {
  const iconOnly = size?.startsWith("icon") ?? false;

  const spinner = (
    <HugeiconsIcon
      icon={Loading03Icon}
      data-icon={iconOnly ? undefined : "inline-end"}
      className="animate-spin motion-reduce:animate-none"
      aria-hidden="true"
    />
  );

  return (
    <ButtonPrimitive
      data-slot="button"
      // Render-prop triggers replace data-slot; these retain the button's
      // appearance metadata through tooltip, menu and dialog composition.
      data-button-variant={variant}
      data-button-size={size}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      // A press that starts the work must not drop focus to the page.
      focusableWhenDisabled={focusableWhenDisabled || loading}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {loading && iconOnly ? (
        spinner
      ) : (
        <>
          {children}
          {loading && spinner}
        </>
      )}
    </ButtonPrimitive>
  );
}

export { Button };
